import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { useHubToast } from "@/components/ui/HubToast";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Input } from "@/components/ui/Input";
import { HoverHintWithGlyph, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { apiFetch, ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

const DERIVED_DEMAND_SETTINGS_HINT =
  "Per category, choose which upstream SKU(s) the MRP forecast engine generates demand on. " +
  "For coated clamps this is the TLA assembly (e.g. small_coated → 90-002-TLA) because the leaf " +
  "purchased SKU (90-002-04) only appears under the 90-002-QC AML BOM, which the depth-1 forecast " +
  "engine does not traverse. Leave a category empty to use the default — the fg_sku for non-kit " +
  "categories and each kit component SKU for kits. Each upstream SKU's MRP forecast raw quantity " +
  "feeds the middle line in clamp forecast cells.";

const DERIVED_DEMAND_EDITOR_HINT =
  "Replaces the override list for this category. Save with an empty list to revert to defaults.";

interface ClampForecastCategoryRow {
  code: string;
  display_name: string;
  sort_order: number;
  is_kit: boolean;
  fg_sku: string | null;
  notes: string | null;
  active: boolean;
  updated_at: string;
  updated_by: string | null;
}

interface ClampForecastDerivedDemandSkuRow {
  category_code: string;
  sku: string;
  sort_order: number;
  is_default: boolean;
  notes: string | null;
}

function describeApiError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}

function indexDerivedDemandSkusByCategory(
  rows: ClampForecastDerivedDemandSkuRow[],
): Map<string, ClampForecastDerivedDemandSkuRow[]> {
  const map = new Map<string, ClampForecastDerivedDemandSkuRow[]>();
  for (const row of rows) {
    const list = map.get(row.category_code) ?? [];
    list.push(row);
    map.set(row.category_code, list);
  }
  return map;
}

export function ClampForecastSettingsSection(): JSX.Element {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const { showToast } = useHubToast();
  const [openCategory, setOpenCategory] = useState<string | null>(null);

  const categoriesQuery = useQuery({
    queryKey: ["mrp", "clamp-forecast", "categories"],
    queryFn: () =>
      apiFetch<{
        categories: ClampForecastCategoryRow[];
        derived_demand_skus: ClampForecastDerivedDemandSkuRow[];
      }>("/api/mrp/clamp-forecast/categories"),
  });

  const visibleCategories = useMemo(
    () => (categoriesQuery.data?.categories ?? []).filter((category) => category.active),
    [categoriesQuery.data?.categories],
  );

  const derivedDemandSkusByCategory = useMemo(
    () => indexDerivedDemandSkusByCategory(categoriesQuery.data?.derived_demand_skus ?? []),
    [categoriesQuery.data?.derived_demand_skus],
  );

  const handleGridStale = () => {
    void queryClient.invalidateQueries({ queryKey: ["mrp", "clamp-forecast"] });
    void queryClient.invalidateQueries({ queryKey: ["mrp", "clamp-forecast", "categories"] });
  };

  if (categoriesQuery.isError) {
    return (
      <HubSettingsSection title="Derived-demand SKU mapping" hoverHint={DERIVED_DEMAND_SETTINGS_HINT}>
        <p className="text-sm text-rose-800" role="alert">
          Could not load clamp forecast categories: {describeApiError(categoriesQuery.error)}
        </p>
      </HubSettingsSection>
    );
  }

  if (categoriesQuery.isLoading) {
    return (
      <HubSettingsSection title="Derived-demand SKU mapping" hoverHint={DERIVED_DEMAND_SETTINGS_HINT}>
        <p className="text-sm text-slate-500">Loading categories…</p>
      </HubSettingsSection>
    );
  }

  return (
    <HubSettingsSection title="Derived-demand SKU mapping" hoverHint={DERIVED_DEMAND_SETTINGS_HINT}>
      <ul className="divide-y divide-slate-200">
        {visibleCategories.map((category) => {
          const rows = derivedDemandSkusByCategory.get(category.code) ?? [];
          const isOverride = rows.length > 0 && rows.every((row) => !row.is_default);
          const isOpen = openCategory === category.code;
          return (
            <li key={category.code} className="py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900">{category.display_name}</div>
                  <div className="font-mono text-[11px] text-slate-500">
                    {category.is_kit ? "(kit)" : category.fg_sku ?? "—"}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px]">
                    {rows.length === 0 ? (
                      <span className="text-slate-500">No upstream SKUs resolved (no defaults available).</span>
                    ) : (
                      rows.map((row) => (
                        <span
                          key={row.sku}
                          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono ${
                            row.is_default ? "bg-slate-100 text-slate-600" : "bg-brand-50 text-brand-800"
                          }`}
                        >
                          {row.sku}
                          {row.is_default ? <span className="text-[10px]">default</span> : null}
                        </span>
                      ))
                    )}
                  </div>
                </div>
                <ReadOnlyHoverLayer
                  active={!hubWritesAllowed}
                  hoverHint="MRP write access is required to edit derived-demand SKU mapping."
                  layout="inline"
                >
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!hubWritesAllowed}
                    onClick={() => setOpenCategory(isOpen ? null : category.code)}
                  >
                    {isOpen ? "Hide editor" : isOverride ? "Edit override" : "Add override"}
                  </Button>
                </ReadOnlyHoverLayer>
              </div>
              {isOpen ? (
                <DerivedDemandSkuEditor
                  category={category}
                  initialRows={
                    isOverride
                      ? rows.map((row) => ({ sku: row.sku, sort_order: row.sort_order, notes: row.notes }))
                      : []
                  }
                  hubWritesAllowed={hubWritesAllowed}
                  onClose={() => setOpenCategory(null)}
                  onGridStale={handleGridStale}
                  onError={(message) => showToast({ message, tone: "error" })}
                  onSuccess={(message) => showToast({ message, tone: "success" })}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </HubSettingsSection>
  );
}

interface DerivedDemandSkuEditorRow {
  sku: string;
  sort_order: number;
  notes: string | null;
}

function derivedDemandRowsEqual(
  left: DerivedDemandSkuEditorRow[],
  right: DerivedDemandSkuEditorRow[],
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  for (let index = 0; index < left.length; index += 1) {
    const leftRow = left[index];
    const rightRow = right[index];
    if (
      leftRow.sku !== rightRow.sku ||
      leftRow.sort_order !== rightRow.sort_order ||
      leftRow.notes !== rightRow.notes
    ) {
      return false;
    }
  }
  return true;
}

interface DerivedDemandSkuEditorProps {
  category: ClampForecastCategoryRow;
  initialRows: DerivedDemandSkuEditorRow[];
  hubWritesAllowed: boolean;
  onClose: () => void;
  onGridStale: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

function DerivedDemandSkuEditor({
  category,
  initialRows,
  hubWritesAllowed,
  onClose,
  onGridStale,
  onError,
  onSuccess,
}: DerivedDemandSkuEditorProps): JSX.Element {
  const [rows, setRows] = useState<DerivedDemandSkuEditorRow[]>(initialRows);
  const isDirty = useMemo(() => !derivedDemandRowsEqual(rows, initialRows), [initialRows, rows]);
  const saveMutation = useMutation({
    mutationFn: async () => {
      return apiFetch<{ rows: ClampForecastDerivedDemandSkuRow[] }>(
        `/api/mrp/clamp-forecast/categories/${encodeURIComponent(category.code)}/derived-demand-skus`,
        {
          method: "PUT",
          body: {
            rows: rows
              .map((row, index) => ({
                sku: row.sku.trim(),
                sort_order: Number.isFinite(row.sort_order) ? row.sort_order : index * 10,
                notes: row.notes,
              }))
              .filter((row) => row.sku.length > 0),
          },
        },
      );
    },
    onSuccess: () => {
      onSuccess(`Saved derived-demand SKU mapping for ${category.display_name}.`);
      onGridStale();
      onClose();
    },
    onError: (error) => onError(describeApiError(error)),
  });
  const writeDisabled = !hubWritesAllowed;
  return (
    <div className="mt-2 space-y-2 rounded border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <h3 className="text-xs font-semibold text-slate-800">Override rows</h3>
        <HoverHintWithGlyph size="sm" hoverHint={DERIVED_DEMAND_EDITOR_HINT} />
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-500">No override rows. Add one below or save to use defaults.</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((row, index) => (
            <li key={index} className="grid gap-1 sm:grid-cols-[1fr_5rem_2fr_auto]">
              <Input
                value={row.sku}
                placeholder="Upstream SKU (e.g. 90-002-TLA)"
                onChange={(event) =>
                  setRows((current) =>
                    current.map((existing, idx) =>
                      idx === index ? { ...existing, sku: event.target.value } : existing,
                    ),
                  )
                }
                disabled={writeDisabled}
              />
              <Input
                type="number"
                value={String(row.sort_order)}
                onChange={(event) =>
                  setRows((current) =>
                    current.map((existing, idx) =>
                      idx === index
                        ? { ...existing, sort_order: Number.parseInt(event.target.value, 10) || 0 }
                        : existing,
                    ),
                  )
                }
                disabled={writeDisabled}
              />
              <Input
                value={row.notes ?? ""}
                placeholder="Notes (optional)"
                onChange={(event) =>
                  setRows((current) =>
                    current.map((existing, idx) =>
                      idx === index
                        ? { ...existing, notes: event.target.value === "" ? null : event.target.value }
                        : existing,
                    ),
                  )
                }
                disabled={writeDisabled}
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRows((current) => current.filter((_, idx) => idx !== index))}
                disabled={writeDisabled}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap justify-between gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() =>
            setRows((current) => [
              ...current,
              { sku: "", sort_order: (current.length + 1) * 10, notes: null },
            ])
          }
          disabled={writeDisabled}
        >
          Add row
        </Button>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <HubSaveButton
            size="sm"
            dirty={isDirty}
            disabled={writeDisabled}
            blockedHint="MRP write access is required to edit derived-demand SKU mapping."
            isLoading={saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            Save mapping
          </HubSaveButton>
        </div>
      </div>
    </div>
  );
}
